import {
  IDateFilterParams,
  type ICellRendererParams,
} from '@mint-lib/ag-grid/community';

import type { FileSystemNode } from '../types/fileSystem.js';
import type { RagTableType } from '../types/rag.js';
import { isFolderNode } from './fileSystemNode.js';

export const FOLDER_DRAFT_ROW_ID = 'folder-draft';

export const FOLDER_DRAFT_ROWS = [
  { kind: 'draft' },
] as unknown as FileSystemNode[];

type GridNode = { data?: FileSystemNode } | null;

export const isDraftRow = (params: { node?: { rowPinned?: unknown } | null }) =>
  Boolean(params.node?.rowPinned);

export const asFileCellParams = (params: ICellRendererParams<FileSystemNode>) =>
  params as unknown as ICellRendererParams<RagTableType>;

const toSortableString = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean')
    return value.toString();
  if (value instanceof Date) return value.toISOString();

  return JSON.stringify(value) ?? '';
};

const compareValues = (valueA: unknown, valueB: unknown) => {
  if (valueA === valueB) return 0;

  if (valueA == null) return -1;
  if (valueB == null) return 1;

  return toSortableString(valueA).localeCompare(toSortableString(valueB));
};

const folderRank = (node: GridNode) => (isFolderNode(node?.data) ? 0 : 1);

export const foldersFirstComparator =
  (inner: (valueA: unknown, valueB: unknown) => number = compareValues) =>
  (
    valueA: unknown,
    valueB: unknown,
    nodeA: GridNode,
    nodeB: GridNode,
    descending: boolean,
  ) => {
    const rankDiff = folderRank(nodeA) - folderRank(nodeB);

    if (rankDiff !== 0) return descending ? -rankDiff : rankDiff;

    return inner(valueA, valueB);
  };

export const dateFilterParams: IDateFilterParams = {
  comparator: (filterLocalDateAtMidnight: Date, cellValue: string) => {
    if (cellValue == null) return -1;

    const fullDate = new Date(cellValue);

    const dateOnly = new Date(
      fullDate.getFullYear(),
      fullDate.getMonth(),
      fullDate.getDate(),
    );

    if (dateOnly < filterLocalDateAtMidnight) {
      return -1;
    } else if (dateOnly > filterLocalDateAtMidnight) {
      return 1;
    }
    return 0;
  },
  closeOnApply: true,
  buttons: ['apply', 'reset'],
  minValidYear: 2000,
  maxValidYear: 2025,
};
